import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();

  // Clear form fields on component mount to prevent autofill
  useEffect(() => {
    setUsername('');
    setPassword('');
    setError('');
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const success = await login(username, password);
      if (!success) {
        setError('Invalid Account or Password');
      }
    } catch (error) {
      setError('Invalid Account or Password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-cyan-400 via-blue-500 to-blue-800">
      {/* Header - Complementary theme */}
      <header className="p-4 bg-gradient-to-r from-cyan-500 via-blue-600 to-blue-700 border-b border-cyan-300/20">
        <div className="flex flex-col items-center space-y-2">
          <img
            src="/PipescanAILogo/SVG/full_dark.svg"
            alt="Pipescan AI Logo"
            className="h-10 rounded"
          />
          <h1 className="text-xl text-white text-center">
            Real-time Monitoring Dashboard
          </h1>
        </div>
      </header>

      {/* Login Form - Centered */}
      <div className="flex-1 flex items-center justify-center">
        <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 p-8 rounded-2xl shadow-2xl w-96 border border-blue-400/30 backdrop-blur-sm">
          <form
            onSubmit={handleSubmit}
            className="space-y-4 w-full"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
          >
            {/* Login Title */}
            <div className="text-center">
              <h2 className="text-2xl font-bold text-white mb-6">Login</h2>
            </div>

            {/* Account Field */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-cyan-100 mb-2">
                Account:
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 bg-white/10 border border-cyan-300/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 focus:border-transparent text-white placeholder-cyan-200/70 backdrop-blur-sm transition-all duration-300 hover:bg-white/15"
                required
                disabled={isLoading}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                data-form-type="other"
              />
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-cyan-100 mb-2">
                Password:
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-white/10 border border-cyan-300/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-300 focus:border-transparent text-white placeholder-cyan-200/70 backdrop-blur-sm transition-all duration-300 hover:bg-white/15"
                required
                disabled={isLoading}
                autoComplete="new-password"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                data-form-type="other"
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="text-red-300 text-sm text-center bg-red-900/30 p-3 rounded-lg border border-red-400/30 animate-pulse">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-cyan-400 via-cyan-500 to-blue-600 text-white py-3 px-6 rounded-lg hover:from-cyan-300 hover:via-cyan-400 hover:to-blue-500 hover:scale-105 hover:shadow-2xl hover:shadow-cyan-400/30 focus:outline-none focus:ring-2 focus:ring-cyan-300 focus:ring-offset-2 focus:ring-offset-transparent disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all duration-300 shadow-lg font-semibold text-lg"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Logging in...
                  </div>
                ) : (
                  'Login'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Footer - Complementary theme */}
      <footer className="p-2 bg-gradient-to-r from-cyan-500 via-blue-600 to-blue-700 text-sm text-white text-center mt-auto border-t border-cyan-300/20">
        Water Barrier Monitoring Solution
      </footer>
    </div>
  );
};

export default Login;
